import { createContext, useContext, useState, useEffect, useCallback } from 'react';

import en from './translations/en.json';
import es from './translations/es.json';
import fr from './translations/fr.json';
import ar from './translations/ar.json';
import zh from './translations/zh.json';
import hi from './translations/hi.json';
import ur from './translations/ur.json';
import de from './translations/de.json';
import pt from './translations/pt.json';
import ja from './translations/ja.json';
import tr from './translations/tr.json';

const translations = { en, es, fr, ar, zh, hi, ur, de, pt, ja, tr };

export const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', dir: 'ltr' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰', dir: 'rtl' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'pt', name: 'Português', flag: '🇧🇷', dir: 'ltr' },
  { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
];

// Country code to language mapping for auto-detection
const COUNTRY_LANG_MAP = {
  US: 'en', GB: 'en', AU: 'en', NZ: 'en', CA: 'en', IE: 'en',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es', SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es',
  FR: 'fr', BE: 'fr', CH: 'fr', SN: 'fr', CI: 'fr', ML: 'fr', CM: 'fr',
  SA: 'ar', AE: 'ar', EG: 'ar', IQ: 'ar', MA: 'ar', DZ: 'ar', SD: 'ar', SY: 'ar', TN: 'ar', YE: 'ar', JO: 'ar', LB: 'ar', LY: 'ar', KW: 'ar', OM: 'ar', QA: 'ar', BH: 'ar',
  CN: 'zh', TW: 'zh', HK: 'zh', SG: 'zh',
  IN: 'hi',
  PK: 'ur',
  DE: 'de', AT: 'de',
  BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt',
  JP: 'ja',
  TR: 'tr',
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('trackly_lang') || 'en';
  });

  const currentTranslation = translations[language] || translations.en;

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem('trackly_lang', lang);
      const dir = translations[lang].dir || 'ltr';
      document.documentElement.setAttribute('dir', dir);
      document.documentElement.setAttribute('lang', lang);
    }
  }, []);

  // Apply dir and lang on mount
  useEffect(() => {
    const dir = currentTranslation.dir || 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
  }, [language, currentTranslation]);

  // Auto-detect language from IP geolocation on first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('trackly_lang');
    if (hasVisited) return;

    const detectLanguage = async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/platform/detect-language`);
        if (res.ok) {
          const data = await res.json();
          if (data.language && translations[data.language]) {
            setLanguage(data.language);
            return;
          }
        }
      } catch {}

      // Fallback: browser language
      const browserLang = navigator.language?.split('-')[0];
      if (browserLang && translations[browserLang]) {
        setLanguage(browserLang);
        return;
      }

      // Fallback: timezone-based
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const tzCountryMap = {
          'Asia/Karachi': 'PK', 'Asia/Kolkata': 'IN', 'Asia/Tokyo': 'JP',
          'Asia/Shanghai': 'CN', 'Asia/Riyadh': 'SA', 'Asia/Dubai': 'AE',
          'Europe/Berlin': 'DE', 'Europe/Paris': 'FR', 'Europe/Madrid': 'ES',
          'Europe/Istanbul': 'TR', 'Europe/Lisbon': 'PT',
          'America/Sao_Paulo': 'BR', 'America/Mexico_City': 'MX',
          'America/Buenos_Aires': 'AR', 'America/Bogota': 'CO',
        };
        const countryCode = tzCountryMap[tz];
        if (countryCode && COUNTRY_LANG_MAP[countryCode]) {
          setLanguage(COUNTRY_LANG_MAP[countryCode]);
        }
      } catch {}
    };

    detectLanguage();
  }, [setLanguage]);

  // Translation function with dot notation path support
  const t = useCallback((path, fallback) => {
    const keys = path.split('.');
    let result = currentTranslation;
    for (const key of keys) {
      result = result?.[key];
      if (result === undefined) break;
    }
    if (result === undefined) {
      // Fallback to English
      let enResult = translations.en;
      for (const key of keys) {
        enResult = enResult?.[key];
        if (enResult === undefined) break;
      }
      return enResult || fallback || path;
    }
    return result;
  }, [currentTranslation]);

  const isRTL = currentTranslation.dir === 'rtl';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isRTL, translations: currentTranslation, LANGUAGES, COUNTRY_LANG_MAP }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
